import { AnalyticsContext, AnalyticsProduct } from 'utils/analytics/events';
import {
  CartContext,
  ContextCache,
  DispensaryContext,
  EcommGTMContext,
  GTMCartItem,
  UserContext,
} from '../internal-gtm-tracker.types';

export type StoreNameDispensary = {
  name?: string | null;
  googleTagManager?: {
    flags?: {
      store_name_override?: string | null;
    } | null;
  } | null;
};

type EcommerceGTMMetaData = {
  context: AnalyticsContext;
  contextCache: ContextCache;
  payload?: {
    cartId?: string;
    products?: AnalyticsProduct[];
    dispensaryId?: string;
    dispensaryName?: string;
  };
  useV2?: boolean;
};

/** Build ecommerce metadata for GTM. When useV2 is true, returns numeric value; otherwise returns legacy string. */
export const ecommerceGTMMetaData = ({
  context,
  contextCache,
  payload,
  useV2,
}: EcommerceGTMMetaData): EcommGTMContext => {
  const affiliation =
    getStoreNameFromDispensary(context.activeDispensary) ??
    getStoreNameFromDispensary(contextCache.activeDispensary) ??
    payload?.dispensaryName;

  if (!useV2) {
    return {
      affiliation,
      currency: 'USD',
      value: context.activeCart?.subtotal?.toFixed(2) ?? contextCache.activeCart?.subtotal?.toFixed(2),
    };
  }

  const subtotal = context.activeCart?.subtotal ?? contextCache.activeCart?.subtotal;
  return {
    affiliation,
    currency: 'USD',
    value: subtotal != null && !Number.isNaN(Number(subtotal)) ? parseFloat(Number(subtotal).toFixed(2)) : undefined,
  };
};

export const cartItemContext = (
  context: AnalyticsContext,
  contextCache: ContextCache,
  payload?: { cartId: string; products: AnalyticsProduct[]; dispensaryId: string; dispensaryName: string }
): EcommGTMContext => ({
  affiliation:
    getStoreNameFromDispensary(context.activeDispensary) ??
    getStoreNameFromDispensary(contextCache.activeDispensary) ??
    payload?.dispensaryName,
});

type CartItemLike = {
  basePrice?: number | string;
  unitPrice?: number | string;
  price?: number | string;
  option?: string | null;
  quantity?: number | string;
  additionalOption?: string;
  trackerSource?: string | null;
  placement?: string | null;
  provider?: string | null;
  product?: {
    id?: string | null;
    Name?: string | null;
    type?: string | null;
    category?: string | null;
    subcategory?: string | null;
    Options?: (string | null)[] | null;
    Prices?: (number | string | null)[] | null;
    brand?: { name?: string | null } | null;
    brandName?: string | null;
    provider?: string | null;
  } | null;
};

/** Resolve the correct price for a cart item based on the selected variant/option */
function resolveItemPrice(item: CartItemLike): number | undefined {
  // Use the pre-computed basePrice (from CartItem class) if available
  if (item.basePrice != null) {
    const parsed = parseFloat(String(item.basePrice));
    if (!Number.isNaN(parsed)) return parsed;
  }

  // Use a pre-set price field if available
  if (item.price != null) {
    const parsed = parseFloat(String(item.price));
    if (!Number.isNaN(parsed)) return parsed;
  }

  // Resolve price from product's Options/Prices arrays based on the selected option
  const { product } = item;
  if (
    product &&
    item.option &&
    product.Options &&
    product.Options.length > 0 &&
    product.Prices &&
    product.Prices.length > 0
  ) {
    const optionIndex = product.Options.indexOf(item.option);
    if (optionIndex >= 0 && product.Prices[optionIndex] != null) {
      const parsed = parseFloat(String(product.Prices[optionIndex]));
      if (!Number.isNaN(parsed)) return parsed;
    }
  }

  // Fall back to the first available price
  if (product?.Prices?.[0] != null) {
    const parsed = parseFloat(String(product.Prices[0]));
    if (!Number.isNaN(parsed)) return parsed;
  }

  return undefined;
}

/** Convert a cart item to GTM format. When useV2 is false, uses the legacy untyped path. */
export const cartItemToGTMCartItem = (item: CartItemLike | any, useV2 = true): GTMCartItem => {
  if (!useV2) {
    return cartItemToGTMCartItemLegacy(item);
  }

  const { product } = item;
  const provider = item.provider ?? product?.provider;
  const placement = item.placement ?? null;

  const gtmCartItem: GTMCartItem = {
    item_id: product?.id ?? '',
    item_name: product?.Name ?? '',
    item_category: product?.type ?? product?.category ?? '',
    item_category_2: product?.subcategory ?? '',
    item_variant: item.option ?? product?.Options?.[0] ?? '',
    item_additional_variant: item.additionalOption,
    item_brand: product?.brand?.name ?? product?.brandName ?? '',
    quantity: item.quantity != null ? Number(item.quantity) : 1,
    price: resolveItemPrice(item),
    item_list_name: item.trackerSource,
  };

  if (provider) {
    gtmCartItem.provider = provider as GTMCartItem['provider'];
  }

  if (placement) {
    gtmCartItem.placement = placement as GTMCartItem['placement'];
  }

  return gtmCartItem;
};

/** Legacy (pre-v2) converter — reads item.product directly, uses resolveItemPrice for correct variant pricing. */
function cartItemToGTMCartItemLegacy(item: any): GTMCartItem {
  const provider = item.provider ?? item.product?.provider;
  const placement = item.placement ?? null;

  const gtmCartItem: GTMCartItem = {
    item_id: item.product?.id ?? '',
    item_name: item.product?.Name ?? '',
    item_category: item.product?.type ?? item.product?.category ?? '',
    item_category_2: item.product?.subcategory ?? '',
    item_variant: item.option ?? item.product?.Options?.[0] ?? '',
    item_additional_variant: item.additionalOption,
    item_brand: item.product?.brand?.name ?? item.product?.brandName ?? '',
    quantity: item.quantity != null ? Number(item.quantity) : 1,
    price: resolveItemPrice(item),
    item_list_name: item.trackerSource,
  };

  if (provider) {
    gtmCartItem.provider = provider;
  }

  if (placement) {
    gtmCartItem.placement = placement;
  }

  return gtmCartItem;
}

export function getStoreNameFromDispensary(dispensary?: StoreNameDispensary | null): string | null | undefined {
  if (!dispensary) {
    return null;
  }

  return dispensary.googleTagManager?.flags?.store_name_override ?? dispensary.name;
}

export const getDispensaryContext = (context: AnalyticsContext, contextCache: ContextCache): DispensaryContext => {
  const state = context.activeDispensary?.location?.state ?? contextCache.activeDispensary?.location?.state;
  return {
    dispensary_id: context.activeDispensary?.id ?? contextCache.activeDispensary?.id,
    dispensary_name:
      getStoreNameFromDispensary(context.activeDispensary) ??
      getStoreNameFromDispensary(contextCache.activeDispensary) ??
      undefined,
    dispensary_state: state as string | undefined,
    dutchie_pay_enabled:
      context.activeDispensary?.isDutchiePayEnabledForDispo ??
      contextCache.activeDispensary?.isDutchiePayEnabledForDispo,
  };
};

export const getUserContext = (context: AnalyticsContext, cache: ContextCache): UserContext => ({
  customer_id: context.activeUser?.id ?? cache.activeUser?.id,
  logged_in: context.activeUser?.isLoggedIn ?? cache.activeUser?.isLoggedIn,
  user_enrolled_dutchie_pay: context.activeUser?.isEnrolledDutchiePay ?? cache.activeUser?.isEnrolledDutchiePay,
});

export const getCartContext = (context: AnalyticsContext, cache: ContextCache): CartContext => ({
  checkout_token: context.activeCart?.checkoutToken ?? cache.activeCart?.checkoutToken,
});

export const getCheckoutToken = (context: AnalyticsContext, contextCache: ContextCache): string | undefined =>
  context.checkoutToken ?? context.activeOrder?.checkoutToken ?? contextCache.activeOrder?.checkoutToken;

export type OrderDiscountFields = {
  manualDiscount?: { fixedDiscountInCents?: number | null } | null;
  mixAndMatch?: number | null;
  credit?: number | null;
};

/**
 * Compute the total dollar discount applied to an order.
 * Sums manualDiscount (fixed cents), mixAndMatch (BOGO/mix specials), and credit (store credit).
 * percentDiscount is intentionally excluded — resolving it to dollars requires the subtotal.
 * Returns null when no discount is present so the field is explicitly absent rather than undefined.
 */
export const getOrderDiscount = (order: OrderDiscountFields | null | undefined): number | null => {
  if (!order) return null;

  const manualFixed = (order.manualDiscount?.fixedDiscountInCents ?? 0) / 100;
  const mixAndMatch = Number(order.mixAndMatch ?? 0);
  const credit = Number(order.credit ?? 0);

  const total = manualFixed + mixAndMatch + credit;
  if (!Number.isFinite(total) || total <= 0) return null;
  return parseFloat(total.toFixed(2));
};

export const getCartDiscount = (context: AnalyticsContext, contextCache: ContextCache): number | null => {
  const summary = context.cartDiscountSummary ?? contextCache.cartDiscountSummary;
  if (!summary) return null;

  const cents = summary.discounts + summary.mixAndMatch + summary.rewards;
  const total = cents / 100;
  if (!Number.isFinite(total) || total <= 0) return null;
  return parseFloat(total.toFixed(2));
};

/** Fields injected into the ecommerce block for all cart/checkout GTM events. */
export const getCommonEcommerceContext = (
  context: AnalyticsContext,
  contextCache: ContextCache
): { checkout_token?: string; coupon: string | null; discount: number | null } => ({
  checkout_token: getCheckoutToken(context, contextCache),
  coupon: context.activeOrder?.coupon?.code ?? null,
  discount: getCartDiscount(context, contextCache),
});
